import { useState, useEffect, useRef, useCallback } from 'react'
import type { ChargepointSummary, ChargepointSummaryWithDistance, UserLocation } from '@fuelfinder/shared'
import { getAllTileGeohashes, calculateDistanceMiles } from '@fuelfinder/shared'
import { fetchAllTiles, fetchNationalDataset } from '../lib/api'

/**
 * Cache stale time in milliseconds (5 minutes)
 */
const CACHE_STALE_TIME = 5 * 60 * 1000

/**
 * Minimum distance in miles before refetching (500m = ~0.31 miles)
 */
const MIN_REFETCH_DISTANCE = 0.31

interface UseTilesReturn {
  chargepoints: ChargepointSummaryWithDistance[]
  isLoading: boolean
  error: string | null
  refetch: () => void
  /** Fetch tiles around an arbitrary point (e.g. map pan target) and merge with existing */
  fetchAroundPoint: (lat: number, lng: number) => void
  /** Load the full national dataset once (for zoomed-out map views) */
  loadNationalDataset: () => void
}

/**
 * Hook for fetching tile-based chargepoint data.
 *
 * - Computes 9 tile geohashes (center + 8 neighbors) based on user location
 * - Fetches tiles in parallel and merges/dedupes chargepoints
 * - Adds distance from user location to each chargepoint
 * - Caches tiles with 5-min stale time
 * - fetchAroundPoint() lets the map trigger loads when user pans
 */
export function useTiles(userLocation: UserLocation | null): UseTilesReturn {
  const [chargepoints, setChargepoints] = useState<ChargepointSummaryWithDistance[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Store all fetched chargepoints deduped by id
  const allCpMapRef = useRef<Map<string, ChargepointSummary>>(new Map())
  // Track which geohash tiles we've already fetched
  const fetchedGeohashesRef = useRef<Set<string>>(new Set())
  // Current user location for distance calc
  const userLocRef = useRef<{ lat: number; lng: number } | null>(null)
  const [fetchTrigger, setFetchTrigger] = useState(0)

  // Whether the full national dataset has been merged in (or is in flight)
  const nationalLoadedRef = useRef(false)

  const refetch = useCallback(() => {
    allCpMapRef.current.clear()
    fetchedGeohashesRef.current.clear()
    nationalLoadedRef.current = false
    setFetchTrigger((prev) => prev + 1)
  }, [])

  /**
   * Recompute distances and update state from the accumulated cp map
   */
  const updateFromMap = useCallback(() => {
    const loc = userLocRef.current
    if (!loc) return

    const withDistance: ChargepointSummaryWithDistance[] = Array.from(allCpMapRef.current.values())
      .map((cp) => ({
        ...cp,
        distanceMiles: calculateDistanceMiles(loc.lat, loc.lng, cp.lat, cp.lng),
      }))
      .sort((a, b) => a.distanceMiles - b.distanceMiles)

    setChargepoints(withDistance)
  }, [])

  /**
   * Fetch tiles around an arbitrary point and merge into existing data.
   * Only fetches geohashes we haven't fetched yet.
   */
  const fetchAroundPoint = useCallback(async (lat: number, lng: number) => {
    // Once the national dataset is loaded, tile fetches add nothing
    if (nationalLoadedRef.current) return
    const geohashes = getAllTileGeohashes(lat, lng)
    const newGeohashes = geohashes.filter((g) => !fetchedGeohashesRef.current.has(g))

    if (newGeohashes.length === 0) return // Already have all these tiles

    try {
      const fetched = await fetchAllTiles(newGeohashes)
      for (const g of newGeohashes) {
        fetchedGeohashesRef.current.add(g)
      }
      for (const cp of fetched) {
        if (!allCpMapRef.current.has(cp.id)) {
          allCpMapRef.current.set(cp.id, cp)
        }
      }
      updateFromMap()
    } catch {
      // Silently ignore pan-fetch errors
    }
  }, [updateFromMap])

  /**
   * Load the complete national dataset once and merge it in.
   * Used when the map zooms out beyond tile-fetch usefulness.
   * On failure the flag resets so a later zoom-out retries.
   */
  const loadNationalDataset = useCallback(async () => {
    if (nationalLoadedRef.current) return
    nationalLoadedRef.current = true
    try {
      const all = await fetchNationalDataset()
      if (all.length === 0) {
        nationalLoadedRef.current = false
        return
      }
      for (const cp of all) {
        if (!allCpMapRef.current.has(cp.id)) {
          allCpMapRef.current.set(cp.id, cp)
        }
      }
      updateFromMap()
    } catch {
      nationalLoadedRef.current = false
    }
  }, [updateFromMap])

  // Initial load when user location changes
  useEffect(() => {
    if (!userLocation) {
      setChargepoints([])
      return
    }

    const { lat, lng } = userLocation
    userLocRef.current = { lat, lng }

    let cancelled = false

    async function loadTiles() {
      setIsLoading(true)
      setError(null)

      try {
        const geohashes = getAllTileGeohashes(lat, lng)
        const newGeohashes = geohashes.filter((g) => !fetchedGeohashesRef.current.has(g))

        if (newGeohashes.length > 0) {
          const fetched = await fetchAllTiles(newGeohashes)
          if (cancelled) return

          for (const g of newGeohashes) {
            fetchedGeohashesRef.current.add(g)
          }
          for (const cp of fetched) {
            if (!allCpMapRef.current.has(cp.id)) {
              allCpMapRef.current.set(cp.id, cp)
            }
          }
        }

        if (cancelled) return
        updateFromMap()
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to fetch chargepoints')
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadTiles()

    return () => {
      cancelled = true
    }
  }, [userLocation, fetchTrigger, updateFromMap])

  return {
    chargepoints,
    isLoading,
    error,
    refetch,
    fetchAroundPoint,
    loadNationalDataset,
  }
}
